import { orderBy } from 'lodash';
import moment from 'moment';
import { createContext, useMemo, useState } from 'react';
import { compose } from 'redux';

import withSelectedEnrolment from '../../../common/hub/NewWithSelectedEnrolment';
import { PATH_HUB_CR_PAUSE_SESSIONS } from '../../../util/pagePath';
import useTimezone from '../../common/hooks/useTimezone';
import MultiStep from '../../common/multi-step';
import api from '../../legacy/api';
import { EnrolmentLayout } from '../../legacy/components';
import { useGetSessionPolicy } from '../../legacy/hooks';
import { Enrolment, Session } from '../../legacy/types';
import LoadingStateContainer from './LoadingStateContainer';
import { PauseEnrolmentProvider } from './PauseEnrolmentContext';
import PauseNotAllowed from './PauseNotAllowed';
import { enrolmentIsPaused, enrolmentWillBePaused } from './form-steps/PauseEnrolmentForm/helpers';
import { PauseSuccess } from './form-steps/PauseSuccess';
import SelectPauseDates from './form-steps/SelectPauseDates';
import SummerDiscountOffer from './form-steps/SummerDiscountOffer';
import SummerDiscountOfferSuccess from './form-steps/SummerDiscountOfferSuccess';
import { canPauseEnrolment, getPauseWindow, isEligibleForSummerDiscount, isSummerPausePromoEnabled } from './helpers';
import useInitializeData from './hooks/useInitializeData';
import useRedirects from './hooks/useRedirects';
import useSchoolPeriods from './hooks/useSchoolPeriods';

export type PauseEnrolmentContextType = {
  enrolmentId: string;
};

type SelectedEnrolment = Enrolment & {
  resumeSessionDate?: string;
};

type SelectedEnrolmentProps = {
  selectedEnrolment: SelectedEnrolment;
};

export type PauseEnrolmentProps = SelectedEnrolmentProps;

export const PauseEnrolmentContext = createContext<PauseEnrolmentContextType | null>(null);

const PauseEnrolment = (props: PauseEnrolmentProps) => {
  useInitializeData();
  useRedirects(props);
  const { selectedEnrolment } = props;
  const enrolmentId = selectedEnrolment.enrolmentSfid;
  const {
    data: sessionPolicy,
    isLoading: isLoadingSessionPolicy,
    error: sessionPolicyQueryError,
  } = useGetSessionPolicy({
    enrolmentId,
  });
  const isFlexi = sessionPolicy?.pause?.pauseAllowed === 'Anytime';
  const {
    data: salesforceCases,
    isLoading: isLoadingSalesforceCases,
    error: salesforceCasesQueryError,
  } = api.salesforceCase.all.useQuery(enrolmentId);
  const hasTakenSummerOffer = salesforceCases?.cases?.length > 0;

  const { data: upcomingSessions = [], isLoading: isLoadingUpcomingSessions } = api.session.all.useQuery('upcoming');
  const timezone = useTimezone();

  const { currentSchoolTerm, nextSchoolTerm, currentOrUpcomingHoliday } = useSchoolPeriods();

  // const { canPauseEarly, startDate: earlyPauseStartDate } = useCanPauseEarly(sessionPolicy, currentOrUpcomingHoliday);
  const { minDate: regularPauseMinDate, maxDate: regularPauseMaxDate } = getPauseWindow(currentOrUpcomingHoliday);
  const willBePaused = enrolmentWillBePaused(selectedEnrolment);
  const isPaused = enrolmentIsPaused(selectedEnrolment);
  const pauseMinDate = useMemo(() => {
    if (willBePaused || isPaused) {
      return moment(selectedEnrolment.enrolmentPausedFrom);
    } else if (isFlexi) {
      return moment().add(1, 'day');
    } else {
      return regularPauseMinDate;
    }
  }, [regularPauseMinDate, willBePaused, selectedEnrolment, isPaused, isFlexi]);
  const pauseMaxDate = regularPauseMaxDate;

  const isPausingForSummerHoliday = currentOrUpcomingHoliday?.type === 'Summer Holiday';
  const summerPausePromoEnabled = isSummerPausePromoEnabled() && isPausingForSummerHoliday;
  const isAllowedToPause = canPauseEnrolment(sessionPolicy, currentOrUpcomingHoliday) || summerPausePromoEnabled;

  const sectionHeading =
    selectedEnrolment.student && selectedEnrolment.subject
      ? `${selectedEnrolment.student} ${selectedEnrolment.subject}`
      : '';
  const mainHeading = summerPausePromoEnabled ? 'Just need a summer break?' : 'Pause enrolment';
  const { data: eligibleSessions } = api.session.all.useQuery('upcoming', {
    select: (data) => {
      const filteredSessions = data.filter(
        (session) =>
          session.uniqueEnrolmentId === selectedEnrolment.enrolmentId &&
          isEligibleForSummerDiscount(session, currentOrUpcomingHoliday)
      );
      return orderBy(filteredSessions, ['startTime'], ['asc']);
    },
  });
  const showPauseInterruptPromo =
    summerPausePromoEnabled && !hasTakenSummerOffer && eligibleSessions?.length > 0 && !isPaused && !willBePaused;

  const [sessionsToApplyDiscountTo, setSessionsToApplyDiscountTo] = useState<Session[]>([]);
  const steps = useMemo(
    () => [
      {
        id: 'select-pause-dates',
        component: (
          <EnrolmentLayout sectionHeading={sectionHeading} mainHeading={mainHeading} enrolmentId={enrolmentId}>
            <SelectPauseDates />
          </EnrolmentLayout>
        ),
      },
      {
        id: 'pause-success',
        component: (
          <EnrolmentLayout>
            <PauseSuccess />
          </EnrolmentLayout>
        ),
      },
      {
        id: 'summer-discount-offer',
        component: (
          <EnrolmentLayout>
            <SummerDiscountOffer
              sessionsToApplyDiscountTo={sessionsToApplyDiscountTo}
              setSessionsToApplyDiscountTo={setSessionsToApplyDiscountTo}
            />
          </EnrolmentLayout>
        ),
      },
      {
        id: 'summer-discount-offer-success',
        component: (
          <EnrolmentLayout>
            <SummerDiscountOfferSuccess sessionsToApplyDiscountTo={sessionsToApplyDiscountTo} />
          </EnrolmentLayout>
        ),
      },
    ],
    [enrolmentId, sectionHeading, mainHeading, sessionsToApplyDiscountTo, setSessionsToApplyDiscountTo]
  );

  if (!isLoadingSessionPolicy && !isAllowedToPause) {
    return (
      <EnrolmentLayout>
        <PauseNotAllowed />
      </EnrolmentLayout>
    );
  }

  return (
    <LoadingStateContainer
      isLoading={isLoadingSessionPolicy || isLoadingUpcomingSessions || isLoadingSalesforceCases}
      failedToLoad={!!sessionPolicyQueryError || !!salesforceCasesQueryError}
    >
      <PauseEnrolmentProvider
        initialState={{
          pauseMethod: !isFlexi || isPaused || willBePaused ? 'pause-by-specific-date' : null,
          specificPauseStartDate: isPaused || willBePaused ? selectedEnrolment.enrolmentPausedFrom : null,
          pauseResumptionMethod: 'resume-on-revision-period',
        }}
        currentSchoolTerm={currentSchoolTerm}
        currentOrUpcomingHoliday={currentOrUpcomingHoliday}
        nextSchoolTerm={nextSchoolTerm}
        selectedEnrolment={selectedEnrolment}
        sessions={upcomingSessions}
        sessionPolicy={sessionPolicy}
        timezone={timezone}
        pauseMinDate={pauseMinDate}
        pauseMaxDate={pauseMaxDate}
      >
        <MultiStep
          steps={steps}
          initialStep={showPauseInterruptPromo ? 'summer-discount-offer' : 'select-pause-dates'}
        />
        ;
      </PauseEnrolmentProvider>
    </LoadingStateContainer>
  );
};

export default compose(withSelectedEnrolment(PauseEnrolment, null, PATH_HUB_CR_PAUSE_SESSIONS));
